import { LOCALIZED_PATHS } from '../../../../../utils/constants/url-constants';
import { buildSrpFacetHref } from './buildSrpFacetHref';
import { getFirstConcreteLocationSegment } from './job-detail-location-parts';

type SupportedLocale = 'en' | 'fi' | 'sv';

function toSupportedLocale(locale: string): SupportedLocale {
  const n = locale.toLowerCase();
  if (n === 'fi' || n === 'sv') return n;
  return 'en';
}

/** Short label for the location breadcrumb (facet value is Algolia-facing, e.g. "Helsinki, Finland"). */
export function formatJobBreadcrumbLocationLabel(locationFacet: string): string {
  const t = locationFacet.trim();
  if (!t) return '';
  const stripped = t.replace(/,\s*(Finland|Suomi)$/i, '').trim();
  return stripped || t;
}

export function resolveJobPageBreadcrumbModel(input: {
  locale: string;
  location: string | string[] | undefined | null;
}): {
  jobsHref: string;
  locationHref: string | null;
  locationLabel: string | null;
} {
  const loc = toSupportedLocale(input.locale);
  const jobsHref = `/${loc}/${LOCALIZED_PATHS[loc].jobs}`;
  const primary = getFirstConcreteLocationSegment(input.location);
  if (!primary.trim()) {
    return { jobsHref, locationHref: null, locationLabel: null };
  }
  return {
    jobsHref,
    locationHref: buildSrpFacetHref(loc, 'location', primary),
    locationLabel: formatJobBreadcrumbLocationLabel(primary),
  };
}

export function buildJobDetailBreadcrumbListSchema(input: {
  baseUrl: string;
  locale: string;
  breadcrumbJobsName: string;
  jobTitle: string;
  jobCanonicalUrl: string;
  location: string | string[] | undefined | null;
}): {
  '@context': string;
  '@type': string;
  itemListElement: Array<{ '@type': string; position: number; name: string; item: string }>;
} {
  const { jobsHref, locationHref, locationLabel } = resolveJobPageBreadcrumbModel({
    locale: input.locale,
    location: input.location,
  });
  const jobsAbsolute = `${input.baseUrl}${jobsHref}`;
  const itemListElement: Array<{ '@type': string; position: number; name: string; item: string }> = [
    { '@type': 'ListItem', position: 1, name: input.breadcrumbJobsName, item: jobsAbsolute },
  ];
  if (locationHref && locationLabel) {
    itemListElement.push({
      '@type': 'ListItem',
      position: 2,
      name: locationLabel,
      item: `${input.baseUrl}${locationHref}`,
    });
  }
  itemListElement.push({
    '@type': 'ListItem',
    position: locationHref && locationLabel ? 3 : 2,
    name: input.jobTitle,
    item: input.jobCanonicalUrl,
  });
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
}
